import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Moon, Sun, Menu, X, Flame } from "lucide-react";
import LiveTicker from "./LiveTicker";
import AdSlot from "./AdSlot";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/matches", label: "Matches" },
  { to: "/standings", label: "Standings" },
  { to: "/news", label: "News" },
  { to: "/teams", label: "Teams" },
];

export const Logo = () => (
  <Link
    to="/"
    data-testid="brand-logo"
    className="group flex items-center gap-2 font-display text-3xl leading-none text-foreground"
  >
    <span className="relative flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
      <Flame size={18} strokeWidth={2.5} />
      <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-foreground animate-live-pulse" />
    </span>
    <span className="tracking-tight">
      Goal<span className="text-primary">Pulse</span>
    </span>
  </Link>
);

const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("gp-theme") || "dark";
  });
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("gp-theme", theme);
  }, [theme]);
  return [theme, setTheme];
};

export const Layout = () => {
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 md:flex" aria-label="Main">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm font-semibold uppercase tracking-wider transition-colors hover:text-primary ${
                    isActive ? "text-primary" : "text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="flex h-9 w-9 items-center justify-center border border-border bg-card text-foreground hover:border-primary hover:text-primary"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              data-testid="menu-toggle"
              aria-label="Open menu"
              onClick={() => setOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center border border-border bg-card md:hidden"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t border-border bg-background md:hidden">
            <nav className="flex flex-col px-4 py-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-2 text-sm font-semibold uppercase tracking-wider ${
                      isActive ? "text-primary" : "text-foreground"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
        <LiveTicker />
      </header>

      <main data-testid="main-content">
        <Outlet />
      </main>

      <footer className="mt-16 border-t border-border bg-card/60">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <AdSlot size="leaderboard" slotId="footer" className="mb-10" />
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <Logo />
              <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                High-energy football coverage, live scores and tactical analysis from across
                Europe&apos;s top five leagues.
              </p>
            </div>
            <div>
              <h4 className="mb-3 overline-eyebrow text-muted-foreground">Sections</h4>
              <ul className="space-y-2 text-sm">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="link-underline text-foreground hover:text-primary">
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="mb-3 overline-eyebrow text-muted-foreground">Leagues</h4>
              <ul className="space-y-2 text-sm text-foreground">
                <li>Premier League</li>
                <li>La Liga</li>
                <li>Bundesliga</li>
                <li>Serie A</li>
                <li>Ligue 1</li>
                <li>UEFA Champions & Europa League</li>
                <li>MLS · Saudi Pro · Liga 1 Indonesia</li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 overline-eyebrow text-muted-foreground">Advertise with us</h4>
              <p className="text-sm text-muted-foreground">
                Reach a passionate football audience. Multiple AdSense & direct banner inventory
                available across our key pages.
              </p>
              <a
                href="mailto:ads@goalpulse.example"
                className="mt-3 inline-flex items-center gap-2 border border-primary px-4 py-2 overline-eyebrow text-primary hover:bg-primary hover:text-primary-foreground"
                data-testid="advertise-cta"
              >
                Get Rate Card
              </a>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start justify-between gap-2 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row">
            <span>© {new Date().getFullYear()} GoalPulse. Live match data via ESPN.</span>
            <Link to="/ads-guide" data-testid="ads-guide-link" className="link-underline hover:text-primary">Ad placement guide</Link>
            <span>Built for fans who live and breathe the beautiful game.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
